/**
 * `POST /ai/analyze`, and the only poll the contract offers for it.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THERE IS NO JOB-STATUS ENDPOINT, AND THE FORM SAYS SO
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * §8.9 publishes four AI operations and none of them reports on a job. The poll is
 * `GET /ai/insights?job_id=<id>`: an empty page means the worker has not written its
 * row yet, and a page of one means it has — **whatever that row says**.
 *
 * So this screen can distinguish "not written yet" from "written", and it *cannot*
 * distinguish queued from running from a worker that died. That is said on the panel
 * rather than expressed as a spinner that turns for ever, because a spinner is a
 * promise the client cannot keep.
 *
 * **A failed job is not an error here.** A quota-refused analysis is a stored row with
 * `status: 'failed'` and `ai_unavailable: false`; a validation failure is a `fallback`
 * carrying its diagnosis. Both are answers, both are rendered by `InsightCard`, and
 * neither is a `5xx` — throwing one away would discard the only thing the caller can
 * use, the numbers, for a sentence they can do nothing about.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  AN ABSENT FILTER IS AN OMITTED MEMBER
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `AiAnalyzeRequest` is `additionalProperties: false`. An empty `employee_ids` is a
 * **different request** from no `employee_ids`, and only the second means "everybody
 * in the caller's resolved scope" — the rule Task 53 §12 had to establish after the
 * `ExportSlot` body was refused outright. So the field is omitted, never `[]` and
 * never `null`.
 *
 * `GET /employees` (Task T2-A) and `EmployeePicker` (Task T2-B) exist now, but this
 * form has not been rewired to use them, so `employee_ids` is still an id field
 * rather than a picker. Named on the field, not left for somebody to discover.
 */

import { useCallback, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { DateField } from '../../components/DateField';
import { EmployeePicker, type EmployeePickerValue } from '../../components/EmployeePicker';
import { AI_ANALYZE_PATH, aiInsightByJobPath } from '../../api/paths';
import type {
  AcceptedJob,
  AiAnalyzeRequest,
  AiInsightListResponse,
  AiInsightTypeName,
} from '../../api/types';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import { readAiRefusal } from '../../ai/refusal';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { InsightCard } from './InsightCard';
import { RefusalPanel } from './RefusalPanel';

const ANALYZE_TYPES: readonly AiInsightTypeName[] = [
  'summary',
  'trend_comparison',
  'anomaly',
  'cause_hypothesis',
  'recommendation',
  'workload_analysis',
];

const TYPE_KEY = {
  summary: 'm4.type.summary',
  trend_comparison: 'm4.type.trend_comparison',
  anomaly: 'm4.type.anomaly',
  cause_hypothesis: 'm4.type.cause_hypothesis',
  recommendation: 'm4.type.recommendation',
  workload_analysis: 'm4.type.workload_analysis',
  scoped_query: 'm4.type.scoped_query',
} as const;

/** The poll, as its own component so its request only exists once a job id does. */
function JobPoll({
  jobId,
  onOpenEvidence,
}: {
  readonly jobId: string;
  readonly onOpenEvidence?: (insightId: string) => void;
}): ReactNode {
  const { t } = useI18n();
  const [token, setToken] = useState(0);
  const query = useApiQuery<AiInsightListResponse>(aiInsightByJobPath(jobId), token);
  const rows = query.data?.data ?? [];

  return (
    <div data-testid="m4-analyze-poll">
      <button
        type="button"
        className="wp-button"
        data-testid="m4-analyze-poll-button"
        onClick={() => {
          setToken((value) => value + 1);
        }}
      >
        {query.status === 'loading' ? t('m4.analyze.polling') : t('m4.analyze.poll')}
      </button>
      <p className="wp-small wp-muted" data-testid="m4-analyze-no-job-status">
        {t('m4.analyze.noJobStatus')}
      </p>
      {query.status === 'loading' ? (
        <Skeleton rows={2} label={t('m4.analyze.pollResult')} testId="m4-analyze-skeleton" />
      ) : query.status === 'error' && query.error !== null ? (
        <RefusalPanel
          refusal={readAiRefusal(query.error)}
          error={query.error}
          subject={t('m4.subject.insights')}
          onRetry={query.reload}
        />
      ) : rows.length === 0 ? (
        // "Not yet", never "no result". The distinction is the whole reason this
        // sentence exists rather than an `EmptyState`.
        <StatusNote
          severity="info"
          title={t('m4.analyze.pollEmpty')}
          testId="m4-analyze-poll-empty"
        />
      ) : (
        <>
          <h3 className="wp-section__title">{t('m4.analyze.pollResult')}</h3>
          {rows.map((row) => (
            <InsightCard
              key={row.id}
              row={row as unknown as Readonly<Record<string, unknown>>}
              {...(onOpenEvidence === undefined ? {} : { onOpenEvidence })}
            />
          ))}
        </>
      )}
    </div>
  );
}

export interface AnalyzeFormProps {
  readonly defaultFrom: string;
  readonly defaultTo: string;
  readonly onOpenEvidence?: (insightId: string) => void;
}

export function AnalyzeForm({
  defaultFrom,
  defaultTo,
  onOpenEvidence,
}: AnalyzeFormProps): ReactNode {
  const { t } = useI18n();
  const [type, setType] = useState<AiInsightTypeName>('summary');
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [employee, setEmployee] = useState<EmployeePickerValue | null>(null);
  const mutation = useApiMutation<AiAnalyzeRequest, AcceptedJob>(AI_ANALYZE_PATH);

  const submit = useCallback(() => {
    void mutation.submit({
      insight_type: type,
      period_start: from,
      period_end: to,
      // Omitted, never `[]`: an empty array is a different request and would mean
      // "nobody" rather than "everybody in the resolved scope".
      ...(employee === null ? {} : { employee_ids: [employee.id] }),
    });
  }, [mutation, type, from, to, employee]);

  const jobId = mutation.data?.job_id ?? null;

  return (
    <section className="wp-section" aria-labelledby="m4-analyze-heading" data-testid="m4-analyze">
      <h2 className="wp-section__title" id="m4-analyze-heading">
        {t('m4.analyze.title')}
      </h2>
      <p className="wp-small">{t('m4.analyze.body')}</p>

      <div className="wp-field">
        <label className="wp-field__label" htmlFor="m4-analyze-type">
          {t('m4.analyze.type')}
        </label>
        <select
          className="wp-field__input"
          id="m4-analyze-type"
          data-testid="m4-analyze-type"
          value={type}
          onChange={(event) => {
            setType(event.target.value as AiInsightTypeName);
          }}
        >
          {ANALYZE_TYPES.map((entry) => (
            <option key={entry} value={entry}>
              {t(TYPE_KEY[entry])}
            </option>
          ))}
        </select>
      </div>

      <DateField
        id="m4-analyze-from"
        testId="m4-analyze-from"
        label={t('m4.analyze.from')}
        value={from}
        onChange={setFrom}
      />

      <DateField
        id="m4-analyze-to"
        testId="m4-analyze-to"
        label={t('m4.analyze.to')}
        value={to}
        onChange={setTo}
      />

      {/* A picker, not a typed list of internal ids (finish review, fix 4).
          `EmployeePicker` has been mounted since Task T2-B and the server's own
          resolved scope decides who appears in it. */}
      <EmployeePicker
        label={t('m4.analyze.employeeIds')}
        testId="m4-analyze-employees"
        value={employee}
        onChange={setEmployee}
      />
      <p className="wp-small wp-muted" data-testid="m4-analyze-no-directory">
        {t('m4.analyze.employeeIds.hint')}
      </p>

      <button
        type="button"
        className="wp-button wp-button--primary"
        data-testid="m4-analyze-submit"
        disabled={mutation.status === 'pending'}
        onClick={submit}
      >
        {mutation.status === 'pending' ? t('m4.analyze.submitting') : t('m4.analyze.submit')}
      </button>

      {mutation.status === 'error' && mutation.error !== null ? (
        <RefusalPanel
          refusal={readAiRefusal(mutation.error)}
          error={mutation.error}
          subject={t('m4.subject.insights')}
        />
      ) : null}

      {jobId === null ? null : (
        <>
          <p data-testid="m4-analyze-queued">{t('m4.analyze.queued', { jobId })}</p>
          <JobPoll jobId={jobId} {...(onOpenEvidence === undefined ? {} : { onOpenEvidence })} />
        </>
      )}
    </section>
  );
}
